// Activo Fijo → Registro → Capitalizar desde compras.
//
// El activo nace de la factura, no de lo que alguien recuerde teclear: costo,
// proveedor, fecha y documento salen del comprobante del SRI. Lo único que hay
// que decidir aquí es lo que la factura NO sabe — qué clase de activo es, dónde
// está y quién lo custodia.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, PackagePlus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Categoria, Opcion, af, afRpc, fieldCls, money } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

interface LineaCompra {
  ticketline_id: string; ticket_buy_id: string; fecha_emision: string;
  serie: string | null; proveedor: string | null; numero_linea: number;
  codigo: string | null; descripcion: string; cantidad: number; costo: number;
  compra_contabilizada: boolean;
}

export function CapitalizarAF() {
  const qc = useQueryClient();
  const [busq, setBusq] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState(fechaLocal());
  const [sel, setSel] = useState<Record<string, boolean>>({});
  const [categoria, setCategoria] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [custodio, setCustodio] = useState('');

  const { data: lineas = [], isFetching, refetch } = useQuery<LineaCompra[]>({
    queryKey: ['af-capitalizables', desde, hasta, busq],
    queryFn: () => afRpc<LineaCompra[]>('af_compras_capitalizables',
      { _desde: desde || null, _hasta: hasta || null, _busq: busq || null }),
  });
  const { data: categorias = [] } = useQuery<Categoria[]>({
    queryKey: ['af-categorias-op'],
    queryFn: () => af.list<Categoria>('categorias', '?select=*&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  const { data: ubicaciones = [] } = useQuery<Opcion[]>({
    queryKey: ['af-ubicaciones-op'],
    queryFn: () => af.list<Opcion>('ubicaciones', '?select=id,nombre&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });
  const { data: custodios = [] } = useQuery<Opcion[]>({
    queryKey: ['af-custodios-op'],
    queryFn: () => af.list<Opcion>('custodios', '?select=id,nombre&deleted_at=is.null&es_activo=is.true&order=nombre'),
  });

  const marcadas = lineas.filter((l) => sel[l.ticketline_id]);
  const total = marcadas.reduce((a, l) => a + Number(l.costo), 0);

  const capitalizar = useMutation({
    mutationFn: () => afRpc<{ n: number; sin_asiento: number; creados: { placa: string }[] }>('af_capitalizar', {
      _data: {
        lineas: marcadas.map((l) => ({
          ticketline_id: l.ticketline_id,
          categoria_id: categoria,
          ubicacion_id: ubicacion || null,
          custodio_id: custodio || null,
        })),
      },
    }),
    onSuccess: (r) => {
      const placas = r.creados.map((c) => c.placa).join(', ');
      toast.success(`${r.n} activo(s) creados: ${placas}`);
      if (r.sin_asiento > 0) {
        toast.warning(`${r.sin_asiento} asiento(s) no se generaron — revisa el Mapeo contable`);
      }
      setSel({});
      void refetch();
      void qc.invalidateQueries({ queryKey: ['af-activos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puede = marcadas.length > 0 && !!categoria;

  return (
    <div className="space-y-4 p-6">
      <h1 className="flex items-center gap-2 text-xl font-semibold"><PackagePlus className="h-5 w-5" /> Capitalizar desde compras</h1>
      <p className="max-w-3xl text-sm text-muted-foreground">
        Líneas de facturas de compra que todavía no son un activo fijo. Al capitalizarlas, el activo hereda el <b>costo sin IVA</b>, el <b>proveedor</b>, la <b>fecha</b> y el <b>enlace al comprobante</b>. Lo que está por debajo del umbral de capitalización configurado en Parámetros no aparece: eso es gasto.
      </p>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <Campo label="Desde"><input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></Campo>
          <Campo label="Buscar (proveedor, serie o descripción)">
            <input className={`${fieldCls} w-64`} value={busq} onChange={(e) => setBusq(e.target.value)} placeholder="torno, 001-001-…" />
          </Campo>
          <Button variant="outline" onClick={() => void refetch()}><Search className="mr-1 h-4 w-4" /> Buscar</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            Líneas sin capitalizar ({lineas.length}) {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 w-8"></th>
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Comprobante</th>
              <th className="py-2 pr-3">Proveedor</th><th className="py-2 pr-3">Descripción</th>
              <th className="py-2 pr-3 text-right">Cant.</th><th className="py-2 pr-3 text-right">Costo (sin IVA)</th>
              <th className="py-2 pr-3">Compra</th>
            </tr></thead>
            <tbody>
              {lineas.length === 0 && !isFetching && (
                <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">
                  No hay líneas de compra pendientes de capitalizar en el rango. Recuerda que sólo aparecen las que superan el umbral de capitalización.
                </td></tr>
              )}
              {lineas.map((l) => (
                <tr key={l.ticketline_id} className={`border-b last:border-0 ${sel[l.ticketline_id] ? 'bg-primary/5' : ''}`}>
                  <td className="py-2 pr-3">
                    <input type="checkbox" checked={!!sel[l.ticketline_id]}
                      onChange={(e) => setSel((s) => ({ ...s, [l.ticketline_id]: e.target.checked }))} />
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{l.fecha_emision}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{l.serie ?? '—'}</td>
                  <td className="py-2 pr-3">{l.proveedor ?? '—'}</td>
                  <td className="py-2 pr-3">{l.descripcion}{l.codigo && <span className="ml-2 font-mono text-xs text-muted-foreground">{l.codigo}</span>}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{Number(l.cantidad)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(l.costo)}</td>
                  <td className="py-2 pr-3">
                    {l.compra_contabilizada
                      ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><FileText className="h-3 w-3" /> contabilizada</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" title="La compra aún no tiene asiento: al capitalizar no hay nada que reclasificar todavía">sin asiento</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {marcadas.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Qué falta decidir ({marcadas.length} línea(s) · {money(total)})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">La factura no sabe qué clase de activo es, dónde está ni quién responde por él. Eso se elige aquí y se aplica a todas las líneas marcadas.</p>
            <div className="flex flex-wrap items-end gap-3">
              <Campo label="Categoría *">
                <select className={`${fieldCls} w-64`} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                  <option value="">— Selecciona —</option>
                  {categorias.map((c) => <option key={c.id} value={c.id}>{c.nombre}{c.vida_util_meses ? ` · ${c.vida_util_meses} meses` : ''}</option>)}
                </select>
              </Campo>
              <Campo label="Ubicación">
                <select className={`${fieldCls} w-48`} value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Custodio">
                <select className={`${fieldCls} w-48`} value={custodio} onChange={(e) => setCustodio(e.target.value)}>
                  <option value="">— Sin asignar —</option>
                  {custodios.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Campo>
              <Button isLoading={capitalizar.isPending} disabled={!puede} onClick={() => capitalizar.mutate()}>
                <PackagePlus className="mr-1 h-4 w-4" /> Capitalizar {marcadas.length}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              La vida útil, el método y el valor residual salen de la categoría. La placa se asigna sola con la numeración de la empresa.
              Cada línea se capitaliza <b>una sola vez</b>: al terminar desaparece de la lista.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
